import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { signAsset } from '@/api/assets';
import { uploadSignedAsset } from '@/api/misc';
import { patchDraw } from '@/api/Sherpa/Draw';
import { notifyUser } from '@/store/Toast';
import { sanitizeResponseError } from '@/utils';

type UploadTemplateFunction = (data: { templateFile: File[]; projectId: number; drawId: number }) => Promise<void>;

interface IUseDrawTemplateFileUpload {
  uploadTemplate: UploadTemplateFunction;
  isLoading: boolean;
  uploadSuccess: boolean;
  isError: boolean;
}

export const useDrawTemplateFileUpload = (): IUseDrawTemplateFileUpload => {
  const tdmIdRef = useRef<number | undefined>(undefined);
  const templateIdRef = useRef<number | undefined>(undefined);
  const templateFileRef = useRef<File[] | undefined>(undefined);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const dispatch = useDispatch();

  const uploadAsset = useMutation(
    ({ sign, files }: { sign: any[]; files: File[] }) =>
      Promise.all(sign.map((signedAsset, index) => uploadSignedAsset({ file: files[index], url: signedAsset.url }))),
    {
      onSuccess: (_, { sign, files }) => {
        const assetsFiles = files.map((file: any, index: number) => {
          return {
            name: file.name,
            key: sign[index].fKey,
            contentType: file.type,
            size: file.size,
          };
        });
        updateDrawTemplate.mutate({
          data: {
            templateDocuments: assetsFiles,
          },
          drawId: templateIdRef.current!,
          projectId: tdmIdRef.current!,
        });
      },
      onError: (_error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Failed to upload Template File. Try again.',
            toastType: 'failed',
          })
        );
      },
    }
  );

  const signAssetFiles = useMutation(
    (payloads: {
      signAssetPayload: {
        key: string;
        type: string;
        resource?: string;
      }[];
    }) => Promise.all(payloads.signAssetPayload.map((payload) => signAsset(payload))),
    {
      onSuccess: (data) => {
        uploadAsset.mutate({
          sign: data,
          files: templateFileRef.current!,
        });
      },
      onError: () => {
        dispatch(notifyUser({ showToast: true, toastMessage: 'Failed to sign Template File. Try again.', toastType: 'failed' }));
      },
    }
  );

  const updateDrawTemplate = useMutation((payload: { data: any; drawId: number; projectId: number }) => patchDraw({ ...payload }), {
    onSuccess: () => {
      setUploadSuccess(true);
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const uploadTemplate = useCallback<UploadTemplateFunction>(
    async ({ templateFile, projectId, drawId }) => {
      templateFileRef.current = templateFile;
      tdmIdRef.current = projectId;
      templateIdRef.current = drawId;

      const signPayload = templateFile.map<{
        key: string;
        type: string;
        resource: string;
      }>((file) => ({
        key: file.name,
        type: file.type,
        resource: 'projectDraw',
      }));

      const payload = {
        signAssetPayload: signPayload,
      };

      signAssetFiles.mutate(payload);
    },
    [signAssetFiles]
  );

  return {
    uploadTemplate,
    isLoading: signAssetFiles.isLoading || updateDrawTemplate.isLoading || uploadAsset.isLoading,
    uploadSuccess: uploadSuccess,
    isError: signAssetFiles.isError || updateDrawTemplate.isError || uploadAsset.isError,
  };
};
